import moment from "moment-timezone";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Button from "../../../../../../components/Button";
import Modal from "../../../../../../components/Modal";
import Table from "../../../../../../components/NTable";
import { useNewTable } from "../../../../../../components/NTable/hook";
import { getErrorMessage } from "../../../../../../utils/helpers/errors";
import { useUser } from "../../../../../../utils/hooks/user";
import { useVisible } from "../../../../../../utils/hooks/useVisible";
import { useDeleteModal } from "../../../../../Settings/helpers/useDeleteModal";
import { useTimesheetImportLogs } from "../../../../api/useTimesheetImportLogs";
import ImportSummary from "../../../components/ImportSummary";
import DateFilters from "./DateFilters";
import { useColumns } from "./useColumns";

function ImportLogModal({ visible, close, onSuccess: _onSuccess = () => { } }) {
    const { t } = useTranslation();
    const user = useUser();
    const [filters, setFilters] = useState({
        mode: "week",
        from: moment().startOf("week"),
        to: moment().endOf("week")
    });
    const { from, to } = filters;

    const {
        data,
        loading,
        totalItems,
        revertImport,
        getLogs,
        setData
    } = useTimesheetImportLogs();

    const onSuccess = useCallback((id) => {
        toast.success(t("removed-successfully"));
        setData((prev) => (prev || []).map((item) => {
            if (item.id === id) {
                return {
                    ...item,
                    revertedAt: moment().toISOString(true),
                    revertedBy: user?.id
                }
            }
            return item;
        }));
        _onSuccess?.();
    }, [_onSuccess, setData, t, user?.id]);

    const onFailure = useCallback((error) => {
        const value = getErrorMessage(error, t);
        toast.error(t(value));
    }, [t]);

    const removeItem = useCallback((id) => {
        revertImport(id, onSuccess, onFailure);
    }, [onFailure, onSuccess, revertImport]);

    const { open: openDeleteModal, modal: deleteModal } = useDeleteModal({
        message: t("do-you-want-to-remove-created-timesheets"),
        permission: "timeSheet.canEdit",
        onConfirm: (id) => removeItem(id),
    })

    const {
        visible: summaryVisible,
        open: openSummaryModal,
        close: closeSummaryModal,
        selected: selectedLog,
    } = useVisible();

    const columns = useColumns({ openDeleteModal, openSummaryModal });

    const table = useNewTable({
        data,
        loading,
        totalItems,
        columns,
        enableRowSelection: false
    });

    const pagination = table.getState().pagination;

    useEffect(() => {
        const controller = new AbortController();
        const params = {
            startDate: moment(from).format("YYYY-MM-DD"),
            endDate: moment(to).format("YYYY-MM-DD"),
            page: pagination.pageIndex + 1,
            perPage: pagination.pageSize,
        };
        getLogs(params, controller);
        return () => controller.abort();
    }, [from, to, getLogs, pagination.pageIndex, pagination.pageSize]);

    return (
        <>
            <Modal
                title={<h2>{t("import-logs")}</h2>}
                isOpen={visible}
                toggle={close}
                centered
                size="lg"
            >
                <div className="m-2 mb-3">
                    <DateFilters filters={filters} setFilters={setFilters} />
                    <Table table={table} />
                </div>

                <div className="d-flex justify-content-end m-2">
                    <Button color="dark" onClick={close}>
                        {t("close")}
                    </Button>
                </div>
            </Modal>

            <Modal
                title={<h2>{t("summary")}</h2>}
                isOpen={summaryVisible}
                toggle={closeSummaryModal}
                centered
                size="lg"
            >
                <div className="m-2">
                    <ImportSummary
                        result={selectedLog?.data?.result}
                        onClose={closeSummaryModal}
                    />
                </div>
            </Modal>

            {deleteModal}
        </>
    );
}

export default ImportLogModal;
